import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import { formatDate } from '../../../lib/datetime'
import { formatDuration, shortTime } from '../duration'
import type { MeetingRecord } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İşlem süren satır — sil düğmesi kilitlenir (çift istek olmasın). */
  busyId: number | null
  onEdit: (row: MeetingRecord) => void
  onDelete: (row: MeetingRecord) => void
}

/**
 * Toplantı Kaydı tablosunun sütunları: Kayıt Türü · Ad · Tarih · Başlangıç · Bitiş · Süre · Kategori ·
 * Otorite · işlemler.
 *
 * <p><b>Sütun başlıklarında sıralama YOKTUR</b>: sayfalama sunucu tarafındadır ve sunucu sırayı
 * sabitler (en yeni kayıt üstte). Başlıklara sıralama konsaydı yalnız <b>açık sayfayı</b> sıralar,
 * yani kullanıcıya yanlış söylerdi.
 *
 * <p>Süre hesaplanmış bir alandır (kolonu yoktur) ve "1 sa 30 dk" biçiminde yazılır — ham dakika
 * kullanıcıya bir şey anlatmaz.
 */
export function meetingRecordColumns({ t, busyId, onEdit, onDelete }: ColumnOpts): ReactElement[] {
  const durationLabels = {
    hour: t('timePortfolio.duration.hour'),
    minute: t('timePortfolio.duration.minute'),
  }
  return [
    <Column
      key="kind"
      header={t('timePortfolio.meeting.columns.kind')}
      style={{ width: '8rem' }}
      body={(row: MeetingRecord) => row.meetingOrActivityName ?? ''}
    />,
    <Column key="name" field="name" header={t('timePortfolio.meeting.columns.name')} />,
    <Column
      key="date"
      header={t('timePortfolio.meeting.columns.date')}
      style={{ width: '8rem' }}
      body={(row: MeetingRecord) => formatDate(row.recordDate)}
    />,
    <Column
      key="start"
      header={t('timePortfolio.meeting.columns.start')}
      style={{ width: '6rem' }}
      body={(row: MeetingRecord) => shortTime(row.startTime)}
    />,
    <Column
      key="finish"
      header={t('timePortfolio.meeting.columns.finish')}
      style={{ width: '6rem' }}
      body={(row: MeetingRecord) => shortTime(row.finishTime)}
    />,
    <Column
      key="duration"
      header={t('timePortfolio.meeting.columns.duration')}
      style={{ width: '8rem' }}
      body={(row: MeetingRecord) => formatDuration(row.durationMinutes, durationLabels)}
    />,
    <Column
      key="category"
      header={t('timePortfolio.meeting.columns.category')}
      style={{ width: '10rem' }}
      // Kategori silinmişse ad çözülemez; ham kimlik GÖSTERİLMEZ, hücre boş kalır.
      body={(row: MeetingRecord) => row.meetingTypeName ?? ''}
    />,
    <Column
      key="authority"
      header={t('timePortfolio.meeting.columns.authority')}
      style={{ width: '8rem' }}
      body={(row: MeetingRecord) =>
        row.authorityMandatory ? t('timePortfolio.authority.yes') : t('timePortfolio.authority.no')
      }
    />,
    <Column
      key="actions"
      header={t('timePortfolio.columns.actions')}
      style={{ width: '8rem' }}
      body={(row: MeetingRecord) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('timePortfolio.actions.editNamed', { name: row.name })}
            tooltip={t('timePortfolio.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            aria-label={t('timePortfolio.actions.deleteNamed', { name: row.name })}
            tooltip={t('timePortfolio.actions.delete')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onDelete(row)}
          />
        </div>
      )}
    />,
  ]
}
